import { CompanyUser } from '@/api';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { UIToast } from '@/ui';
import { useCompaniesTranslation } from '../hooks';

type UseResendInviteActionProps = {
    user: CompanyUser;
};

type UseResendInviteActionData = {
    /** Whether this viewer can resend this member's invite right now. */
    isAllowed: boolean;
    /** Why not, ready to show in a tooltip. Empty while allowed. */
    reason: string;
    resendInvite: () => Promise<void>;
    loading: boolean;
    error: string;
};

/**
 * Resending a member's invitation: whether this viewer may do it, why not when
 * they may not, and the call itself. Kept together so a caller can wire up a
 * button — disabled state, tooltip, spinner and click — from one hook.
 */
export function useResendInviteAction(
    props: UseResendInviteActionProps
): UseResendInviteActionData {
    const { user } = props;

    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const canResendInvite = user.capabilities.canResendInvite;
    const [isAllowed, reason] = useMemo(() => {
        // The API decides who is permitted to resend this member's invite, and
        // hands back a ready-to-show reason when they are not.
        if (!canResendInvite.allowed) {
            return [
                false,
                canResendInvite.reason ??
                    t('actions.resend_invite.reason_not_permitted')
            ];
        }

        // There is nothing to resend once the invite has been taken up.
        if (user.joined) {
            return [false, t('actions.resend_invite.reason_already_joined')];
        }

        return [true, ''];
    }, [canResendInvite, user.joined, t]);

    // One place that turns a failure into a message, so the toast and the
    // returned `error` can never disagree. Every error
    // `POST /v3/users/{id}/invites/resend` declares gets a branch.
    const getErrorMessage = useCallback(
        (error: unknown): string => {
            if (isApiError(error, 'USER_ALREADY_JOINED')) {
                return t('actions.resend_invite.error_already_joined');
            }
            if (isApiError(error, 'COMPANY_USER_NOT_FOUND')) {
                return t('actions.resend_invite.error_not_found');
            }
            if (isApiError(error, 'FORBIDDEN')) {
                return t('shared.errors.unauthorised');
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return error.response?.data.message.join(', ') ?? '';
            }

            return t(getFallbackErrorKey(error));
        },
        [t]
    );

    const mutation = useMutation({
        mutationFn: () =>
            openApiRef.current.users.resendUserInvite(user.id).then(() => {}),
        // Nothing to write to the cache: resending changes no record the app
        // reads back.
        onSuccess: () => {
            UIToast.success(t('actions.resend_invite.success'));
        },
        onError: (error) => {
            UIToast.error(getErrorMessage(error));
        }
    });

    return {
        isAllowed,
        reason,
        resendInvite: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error ? getErrorMessage(mutation.error) : ''
    };
}
